import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Bell, 
  Upload, 
  FileText, 
  FileDown, 
  Settings as SettingsIcon,
  LogOut,
  X,
  Menu,
  Shield,
  UserCog,
  Calendar,
  User
} from 'lucide-react';
import '../../styles/mobile-drawer.css';
import { notifyAuthStateChanged } from '../../utils/authSession';

/**
 * MobileDrawer - Hamburger drawer navigation for mobile devices
 * 
 * Provides full navigation parity with desktop sidebar while preserving mobile behaviors
 * Features:
 * - Persistent hamburger header (never disappears)
 * - Slide-out drawer with all desktop modules
 * - Touch-friendly 44px+ targets
 * - RBAC filtering
 * - Logout within 2 taps
 * - Works in mobile browser and WebView wrapper
 */

const MobileDrawer = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  // Navigation items matching desktop Sidebar
  const allNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['all'] },
    { path: '/admin-dashboard', icon: Shield, label: 'Admin Dashboard', roles: ['admin'] },
    { path: '/manager-dashboard', icon: UserCog, label: 'Manager Dashboard', roles: ['manager', 'bm', 'am', 'tl'] },
    { path: '/candidates', icon: Users, label: 'Candidates', roles: ['all'] },
    { path: '/my-followups', icon: Calendar, label: 'My Follow-Ups', roles: ['all'] },
    { path: '/follow-ups', icon: Bell, label: 'Follow-Ups', roles: ['all'] },
    { path: '/upload', icon: Upload, label: 'Upload', roles: ['all'] },
    { path: '/jd-upload', icon: FileText, label: 'Job Descriptions', roles: ['all'] },
    { path: '/reports', icon: FileDown, label: 'Reports', roles: ['admin', 'bm', 'am', 'tl'] },
    { path: '/settings', icon: SettingsIcon, label: 'Settings', roles: ['all'] },
  ];

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => {
    if (item.roles.includes('all')) return true;
    if (!user) return false;
    return item.roles.includes(user.role);
  });

  // Get current path for active state
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  // Handle navigation
  const handleNavClick = (path) => {
    navigate(path);
    setIsDrawerOpen(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('spes_token');
    localStorage.removeItem('spes_role');
    localStorage.removeItem('spes_user');
    notifyAuthStateChanged();
    console.log('✅ Logged out successfully');
    navigate('/login');
    setIsDrawerOpen(false);
  };

  // Get role display
  const getRoleDisplay = (role) => {
    const roleMap = {
      'admin': 'SUPERADMIN',
      'bm': 'BRANCH MGR',
      'am': 'ACCOUNT MGR',
      'tl': 'TEAM LEAD',
      'recruiter': 'RECRUITER'
    };
    return roleMap[role] || role.toUpperCase();
  };

  // Get page title from current path
  const getPageTitle = () => {
    const currentItem = allNavItems.find(item => 
      item.path === currentPath || (item.path !== '/' && currentPath.startsWith(item.path))
    );
    return currentItem ? currentItem.label : 'Spes Recruiter';
  };

  // Close drawer when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isDrawerOpen && !e.target.closest('.mobile-drawer') && !e.target.closest('.mobile-header')) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('click', handleOutsideClick);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  // Handle back button
  useEffect(() => {
    const handleBackButton = (e) => {
      if (isDrawerOpen) {
        e.preventDefault();
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      window.addEventListener('popstate', handleBackButton);
      window.history.pushState(null, null, window.location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [isDrawerOpen]);

  return (
    <>
      {/* Mobile Header - Always Visible */}
      <header className="mobile-header fixed top-0 left-0 right-0 h-14 bg-[#1e3a5f] text-white flex items-center px-4 z-50 md:hidden shadow-lg">
        {/* Hamburger Button */}
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="p-2 rounded-md hover:bg-white/10 transition-colors duration-200 touch-action-manipulation"
          aria-label="Open menu"
          aria-expanded={isDrawerOpen}
        >
          <Menu size={24} />
        </button>

        {/* Page Title */}
        <h1 className="flex-1 text-center text-lg font-semibold px-4 truncate">
          {getPageTitle()}
        </h1>

        {/* Spacer for balance */}
        <div className="w-10"></div>
      </header>

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fade-in"
          aria-hidden="true"
        />
      )}

      {/* Drawer Panel */}
      <div className={`
        mobile-drawer fixed top-0 left-0 h-full w-72 max-w-[80vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden
        ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Drawer Header */}
        <div className="h-14 bg-[#1e3a5f] text-white flex items-center justify-between px-4">
          <span className="text-lg font-semibold">Menu</span>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="p-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* User Info Section */}
        {user && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#f59e0b] rounded-full flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{user.name || 'User'}</p>
                <p className="text-sm text-gray-600">{getRoleDisplay(user.role)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-2">
          {/* Core Operations Section */}
          <div className="px-3 py-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Core Operations
            </p>
            {navItems.filter(item => 
              ['/dashboard', '/candidates', '/my-followups', '/follow-ups', '/upload', '/jd-upload']
              .includes(item.path)
            ).map(item => {
              const Icon = item.icon;
              const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`
                    flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 touch-action-manipulation
                    ${isActive 
                      ? 'bg-[#f59e0b]/10 text-[#f59e0b] font-semibold' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon size={20} />
                  <span className="text-base">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Dashboards & Reports Section */}
          {navItems.some(item => 
            ['/admin-dashboard', '/manager-dashboard', '/reports']
            .includes(item.path)
          ) && (
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Dashboards & Reports
              </p>
              {navItems.filter(item => 
                ['/admin-dashboard', '/manager-dashboard', '/reports']
                .includes(item.path)
              ).map(item => {
                const Icon = item.icon;
                const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`
                      flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 touch-action-manipulation
                      ${isActive 
                        ? 'bg-[#f59e0b]/10 text-[#f59e0b] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon size={20} />
                    <span className="text-base">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Account Section */}
          <div className="px-3 py-2 border-t border-gray-200 mt-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Account
            </p>
            
            {/* Settings */}
            {navItems.find(item => item.path === '/settings') && (
              <Link
                to="/settings"
                onClick={() => handleNavClick('/settings')}
                className={`
                  flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 touch-action-manipulation mb-2
                  ${currentPath === '/settings' 
                    ? 'bg-[#f59e0b]/10 text-[#f59e0b] font-semibold' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
                aria-current={currentPath === '/settings' ? 'page' : undefined}
              >
                <SettingsIcon size={20} />
                <span className="text-base">Settings</span>
              </Link>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 text-red-600 hover:bg-red-50 touch-action-manipulation"
              aria-label="Logout"
            >
              <LogOut size={20} />
              <span className="text-base font-medium">Logout</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Spacer for fixed header on mobile */}
      <div className="h-14 md:hidden" />
    </>
  );
};

export default MobileDrawer;
